// src/components/legal/TermsModal.tsx
// Terms of Service modal — content can be overridden from Firestore (siteContent/termsOfService).

import { useEffect, useState } from 'react';
import { doc, getDoc }         from 'firebase/firestore';
import { db }                  from '../../lib/firebase';
import { useLegalModalStore }  from '../../stores/useLegalModalStore';
import { LegalModal }          from './LegalModal';

// ── Hardcoded fallback content ────────────────────────────────────────────────
const FALLBACK_HTML = `
<h2>1. Acceptance of Terms</h2>
<p>
  By accessing or using the START platform ("Service"), you agree to be bound by these
  Terms of Service ("Terms"). If you do not agree to these Terms, you may not use the
  Service. These Terms apply to all visitors, registered users, and premium subscribers.
</p>

<hr />

<h2>2. Description of Service</h2>
<p>
  START provides sports-focused English language learning materials, including reading
  exercises, listening exercises, vocabulary builders, and progress tracking tools,
  targeted at athletes, coaches, and sports professionals. Some features are available
  free of charge; others require a paid Premium subscription.
</p>

<hr />

<h2>3. Account Registration</h2>
<ul>
  <li>You must provide accurate, current, and complete information during registration.</li>
  <li>You are responsible for maintaining the confidentiality of your password.</li>
  <li>You must notify us immediately at <a href="mailto:support@startlearning.ge">support@startlearning.ge</a> if you suspect unauthorised access to your account.</li>
  <li>You may not create an account on behalf of another person without their consent.</li>
  <li>Accounts are personal and non-transferable.</li>
</ul>

<hr />

<h2>4. Free and Premium Access</h2>
<h3>4.1 Free Tier</h3>
<p>
  Free-tier users may access a limited set of exercises and materials clearly marked as
  "Free." Free access may be subject to usage limits (e.g. number of exercises per day).
</p>

<h3>4.2 Premium Subscription</h3>
<p>
  A Premium subscription unlocks all content and features. Subscriptions are billed on
  a recurring basis (monthly or annually) via Stripe. By subscribing, you authorise us
  to charge your payment method on a recurring basis until you cancel.
</p>

<h3>4.3 Refunds</h3>
<p>
  We offer a <strong>7-day refund window</strong> for new Premium subscriptions.
  Requests outside this window are evaluated on a case-by-case basis.
  Contact <a href="mailto:billing@startlearning.ge">billing@startlearning.ge</a>.
</p>

<h3>4.4 Cancellation</h3>
<p>
  You may cancel your subscription at any time from your account settings.
  Cancellation takes effect at the end of the current billing period.
</p>

<hr />

<h2>5. Acceptable Use</h2>
<p>You agree <strong>not</strong> to:</p>
<ul>
  <li>Use the Service for any unlawful purpose or in violation of any regulations.</li>
  <li>Reproduce, distribute, or resell any paid content without written permission.</li>
  <li>Attempt to reverse-engineer, scrape, or copy the Service or its content.</li>
  <li>Share your account credentials with others.</li>
  <li>Upload or transmit malicious code, spam, or harmful content.</li>
  <li>Interfere with the security or integrity of the Service.</li>
  <li>Use automated tools (bots, crawlers) to access the Service without prior consent.</li>
</ul>

<hr />

<h2>6. Intellectual Property</h2>
<p>
  All content on the Service — including text, graphics, audio recordings, illustrations,
  course materials, and software — is owned by or licensed to START and is protected by
  Georgian and international intellectual property laws. You may access and use content
  solely for your personal, non-commercial learning purposes.
</p>
<p>
  User-generated content (if any) submitted to the platform remains your property, but
  you grant START a non-exclusive, royalty-free licence to use it to operate and improve
  the Service.
</p>

<hr />

<h2>7. Disclaimers</h2>
<p>
  The Service is provided on an <strong>"as is" and "as available"</strong> basis.
  We do not warrant that the Service will be uninterrupted, error-free, or free from
  viruses or other harmful components. Language learning outcomes depend on individual
  effort and are not guaranteed.
</p>

<hr />

<h2>8. Limitation of Liability</h2>
<p>
  To the maximum extent permitted by applicable law, START and its affiliates, officers,
  employees, and agents shall not be liable for any indirect, incidental, special,
  consequential, or punitive damages, or any loss of profits or revenues, whether
  incurred directly or indirectly, arising out of your use of (or inability to use)
  the Service.
</p>

<hr />

<h2>9. Indemnification</h2>
<p>
  You agree to indemnify and hold harmless START and its affiliates from any claims,
  damages, losses, and expenses (including reasonable legal fees) arising out of your
  violation of these Terms or your misuse of the Service.
</p>

<hr />

<h2>10. Termination</h2>
<p>
  We reserve the right to suspend or terminate your account without notice if you violate
  these Terms or engage in conduct we reasonably believe is harmful to the Service or its
  users. Upon termination, your right to access the Service ceases immediately.
</p>

<hr />

<h2>11. Governing Law</h2>
<p>
  These Terms are governed by the laws of <strong>Georgia</strong>. Any disputes shall be
  resolved in the competent courts of Tbilisi, Georgia, unless otherwise required by
  applicable consumer protection law in your jurisdiction.
</p>

<hr />

<h2>12. Changes to These Terms</h2>
<p>
  We may update these Terms from time to time. We will notify you of material changes by
  email or via an in-app notice at least 14 days before the changes take effect.
  Continued use of the Service after that date constitutes acceptance of the updated Terms.
</p>

<hr />

<h2>13. Contact Us</h2>
<p>
  For legal enquiries:<br />
  <strong>START Language Learning</strong><br />
  Tbilisi, Georgia<br />
  <a href="mailto:legal@startlearning.ge">legal@startlearning.ge</a>
</p>
`;

// ── Component ─────────────────────────────────────────────────────────────────
export function TermsModal() {
  const openModal = useLegalModalStore((s) => s.openModal);
  const isOpen    = openModal === 'terms';

  const [htmlContent, setHtmlContent] = useState<string>(FALLBACK_HTML);

  useEffect(() => {
    getDoc(doc(db, 'siteContent', 'termsOfService'))
      .then((snap) => {
        if (snap.exists()) {
          const data = snap.data();
          if (data?.html) setHtmlContent(data.html as string);
        }
      })
      .catch(() => {/* use fallback */});
  }, []);

  return (
    <LegalModal
      isOpen={isOpen}
      title="Terms of Service"
      lastUpdated="1 January 2026"
      htmlContent={htmlContent}
    />
  );
}